import { useLocalStorage } from '@plato/ui-legacy/lib/hooks/use-local-storage';
import { useCallback } from 'react';
import { z } from 'zod';

const STORAGE_KEY = 'plato-hidden-suggestions';
const DEFAULT_HIDE_DAYS = 7;
const DAY_MS = 24 * 60 * 60 * 1000;

// customerId -> expiry epoch ms
const hiddenMapSchema = z.record(z.string(), z.number());
type HiddenMap = z.infer<typeof hiddenMapSchema>;

type UseHiddenSuggestions = {
    isHidden: (customerId: string) => boolean;
    hide: (customerId: string, days?: number) => void;
    unhide: (customerId: string) => void;
};

/**
 * Client-only "hide this calendar suggestion for a week" store. Backed by useLocalStorage, which broadcasts a synthetic `storage` event on write, so
 * every mounted DaySuggestions (the week view mounts one per day) re-renders when a customer is hidden.
 *
 * Expiry is lazy: `isHidden` just compares the stored timestamp to Date.now(), so a suggestion
 * reappears on the next render once its 7 days pass — no timers, nothing to clean up server-side.
 *
 * ponytail: per-device localStorage, no cross-device sync — fine for a throwaway experiment; move to
 * DB if it graduates past validation.
 */
export function useHiddenSuggestions(): UseHiddenSuggestions {
    const [hidden, setHidden] = useLocalStorage<HiddenMap>(STORAGE_KEY, {}, { schema: hiddenMapSchema });

    const isHidden = useCallback(
        (customerId: string): boolean => (hidden[customerId] ?? 0) > Date.now(),
        [hidden],
    );

    const hide = useCallback(
        (customerId: string, days: number = DEFAULT_HIDE_DAYS): void => {
            setHidden((prev) => {
                const now = Date.now();
                // Prune expired entries while we write so the map can't grow forever.
                const pruned = Object.fromEntries(Object.entries(prev).filter(([, expiry]) => expiry > now));
                return { ...pruned, [customerId]: now + days * DAY_MS };
            });
        },
        [setHidden],
    );

    const unhide = useCallback(
        (customerId: string): void => {
            setHidden((prev) => {
                const { [customerId]: _removed, ...rest } = prev;
                return rest;
            });
        },
        [setHidden],
    );

    return { isHidden, hide, unhide };
}
